import { useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { full_name: '', email: '', whatsapp: '', profile_url: '', content_url: '', idea: '', acknowledgement: false, privacy_consent: false, website: '' };
const input = 'w-full px-4 py-3 border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-shadow';

const validate = (f) => {
  if (f.full_name.trim().length < 2) return 'Please enter your full name';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return 'Please enter a valid email address';
  if (f.whatsapp.replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, '').length !== 10) return 'Please enter a valid 10-digit WhatsApp number';
  if (!/^(https?:\/\/)?([a-z0-9-]+\.)?(instagram\.com|linkedin\.com)\/.+/i.test(f.profile_url.trim())) return 'Please enter your Instagram or LinkedIn profile link';
  if (f.idea.trim().length < 20) return 'Please share one creative idea (at least 20 characters)';
  if (!f.acknowledgement) return 'Please confirm you understand this is a voluntary, unpaid collaboration';
  if (!f.privacy_consent) return 'Please agree to the processing of your application details';
  return '';
};

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

const Success = () => (
  <div data-testid="fc-success" className="text-center py-6">
    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-7 h-7 text-green-600" /></div>
    <h3 className="text-xl font-bold text-slate-900">Thanks, we've got your interest!</h3>
    <p className="mt-2 text-slate-600 max-w-md mx-auto">Our founding team will look at your profile, your content and your idea, and reach out if there's a fit.</p>
  </div>
);

export const FoundingCreatorForm = () => {
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (e) => { const { name, value, type, checked } = e.target; setF((x) => ({ ...x, [name]: type === 'checkbox' ? checked : value })); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const msg = validate(f);
    if (msg) { setError(msg); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/careers/founding-creator/apply`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check your details and try again.');
      setDone(true);
    } catch (err) { setError(err.message || 'Something went wrong. Please try again.'); } finally { setBusy(false); }
  };

  if (done) return <Success />;
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="fc-form">
      <input type="text" name="website" value={f.website} onChange={set} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <Field label="Full Name" required><input name="full_name" value={f.full_name} onChange={set} className={input} data-testid="fc-name-input" /></Field>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Email Address" required><input type="email" name="email" value={f.email} onChange={set} className={input} data-testid="fc-email-input" /></Field>
        <Field label="WhatsApp Number" required><input type="tel" inputMode="numeric" name="whatsapp" value={f.whatsapp} onChange={set} placeholder="10-digit mobile" className={input} data-testid="fc-whatsapp-input" /></Field>
      </div>
      <Field label="Instagram or LinkedIn Profile" required><input name="profile_url" value={f.profile_url} onChange={set} placeholder="https://instagram.com/yourhandle" className={input} data-testid="fc-profile-input" /></Field>
      <Field label="Link to Your Best Content"><input name="content_url" value={f.content_url} onChange={set} placeholder="A Reel, post, page or project you're proud of (optional)" className={input} data-testid="fc-content-input" /></Field>
      <Field label="One creative idea for growing Elevate Careers India" required hint={`${f.idea.length}/1500`}>
        <textarea name="idea" value={f.idea} onChange={set} rows={4} maxLength={1500} className={`${input} resize-none`} data-testid="fc-idea-input" />
      </Field>
      <div className="space-y-3 pt-1">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" name="acknowledgement" checked={f.acknowledgement} onChange={set} className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600" data-testid="fc-ack-checkbox" />
          <span className="text-sm text-slate-700">I understand this is a voluntary, unpaid 14-day collaboration with no fixed hours or mandatory deliverables, and that any future paid opportunity would need a separate agreement and is not guaranteed. <span className="text-red-500">*</span></span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" name="privacy_consent" checked={f.privacy_consent} onChange={set} className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600" data-testid="fc-privacy-checkbox" />
          <span className="text-sm text-slate-700">I agree to Elevate Careers processing my application details for this opportunity, as described in the <a href="/privacy" target="_blank" rel="noreferrer" className="text-teal-600 underline" data-testid="fc-privacy-link">Privacy Policy</a>. <span className="text-red-500">*</span></span>
        </label>
      </div>
      {error && <div data-testid="fc-form-error" className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>}
      <button type="submit" disabled={busy} data-testid="fc-submit-btn" className="w-full py-4 rounded-full bg-teal-600 hover:bg-teal-500 text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : 'Submit My Interest'}
      </button>
    </form>
  );
};
